import { ResultadoOrcamento } from "@/services/orcamentoPublico";
import { formatarMoeda, PARCELAS, valorDaParcela } from "./formato";

type Props = {
  unidadeNome?: string;
  turmaNome?: string;
  resultado: ResultadoOrcamento | null;
};

/**
 * Painel de acompanhamento: o total é o mesmo que a API devolveu, e o resumo de
 * pagamento é só simulação — a página pública não lança forma de pagamento.
 */
export function ResumoLateral({ unidadeNome, turmaNome, resultado }: Props) {
  const total = resultado?.totalFinal ?? 0;
  const itens = resultado?.linhas.filter((linha) => linha.selecionado) ?? [];
  const somaItens = itens.reduce((acumulado, linha) => acumulado + linha.valorFinal, 0);

  return (
    <div style={{ display: "grid", gap: 14 }}>
      <section className="op-panel" style={{ overflow: "hidden" }}>
        <Cabecalho titulo="Seu orçamento" />
        <div style={{ padding: "14px 16px" }}>
          <dl style={{ margin: 0 }}>
            <Item rotulo="Unidade" valor={unidadeNome ?? "—"} discreto />
            <Item rotulo="Turma" valor={turmaNome ?? "—"} discreto />
            <Separador />
            <Item
              rotulo={`${itens.length} ${itens.length === 1 ? "item selecionado" : "itens selecionados"}`}
              valor={resultado ? formatarMoeda(somaItens) : "—"}
            />
            <Item
              rotulo="Matrícula"
              valor={resultado ? formatarMoeda(resultado.matriculaValor) : "—"}
            />
            <Item
              rotulo="Descontos"
              valor={
                resultado && resultado.totalDesconto > 0
                  ? `−${formatarMoeda(resultado.totalDesconto)}`
                  : "—"
              }
              cor="var(--op-crimson)"
            />
          </dl>

          <div
            style={{
              background: "var(--op-amber-wash)",
              margin: "12px -16px -14px",
              padding: "14px 16px",
              borderTop: "1px solid rgba(232,174,11,.45)",
            }}
          >
            <div
              style={{
                fontSize: 11,
                fontWeight: 700,
                letterSpacing: ".14em",
                textTransform: "uppercase",
                color: "var(--op-amber-ink)",
              }}
            >
              Total
            </div>
            <div className="op-num" style={{ fontWeight: 800, fontSize: 31, lineHeight: 1.1 }}>
              {formatarMoeda(total)}
            </div>
            {resultado && resultado.totalDesconto > 0 && (
              <div style={{ fontSize: 13, color: "var(--op-amber-ink)", marginTop: 2 }}>
                De <s>{formatarMoeda(resultado.totalBruto)}</s> por este valor
              </div>
            )}
          </div>
        </div>
      </section>

      <section className="op-panel" style={{ overflow: "hidden" }}>
        <Cabecalho titulo="Resumo de pagamento" />
        <div style={{ padding: "14px 16px" }}>
          <dl className="op-num" style={{ display: "grid", gap: 1, fontSize: 13.5, margin: 0 }}>
            <Parcela rotulo="À vista" valor={formatarMoeda(total)} destaque />
            {PARCELAS.map((parcelas) => (
              <Parcela
                key={parcelas}
                rotulo={`${parcelas}x de`}
                valor={formatarMoeda(valorDaParcela(total, parcelas))}
              />
            ))}
          </dl>
        </div>
        <div
          style={{
            padding: "11px 16px",
            fontSize: 12,
            color: "var(--op-faint)",
            borderTop: "1px solid var(--op-line)",
            background: "var(--op-surface-2)",
          }}
        >
          Simulação. As condições e formas de pagamento são fechadas com a secretaria.
        </div>
      </section>
    </div>
  );
}

function Cabecalho({ titulo }: { titulo: string }) {
  return (
    <div
      style={{
        padding: "13px 16px",
        borderBottom: "1px solid var(--op-line)",
        background: "var(--op-surface-2)",
      }}
    >
      <span style={{ fontWeight: 800, fontSize: 15 }}>{titulo}</span>
    </div>
  );
}

function Item({
  rotulo,
  valor,
  discreto,
  cor,
}: {
  rotulo: string;
  valor: string;
  discreto?: boolean;
  cor?: string;
}) {
  return (
    <div
      style={{
        display: "flex",
        justifyContent: "space-between",
        gap: 12,
        fontSize: 14,
        padding: "4px 0",
      }}
    >
      <dt style={{ color: "var(--op-muted)" }}>{rotulo}</dt>
      <dd
        style={{
          margin: 0,
          textAlign: "right",
          fontWeight: discreto ? 500 : 600,
          color: cor ?? (discreto ? "var(--op-muted)" : undefined),
        }}
      >
        {valor}
      </dd>
    </div>
  );
}

function Separador() {
  return <div style={{ height: 1, background: "var(--op-line)", margin: "11px -16px" }} />;
}

function Parcela({ rotulo, valor, destaque }: { rotulo: string; valor: string; destaque?: boolean }) {
  return (
    <div style={{ display: "flex", justifyContent: "space-between", padding: "3px 0" }}>
      <dt style={{ color: "var(--op-muted)" }}>{rotulo}</dt>
      <dd style={{ margin: 0, fontWeight: destaque ? 700 : 400 }}>{valor}</dd>
    </div>
  );
}

export default ResumoLateral;
